import React, { useState, useEffect } from 'react';
import { GlassButton } from '../ui/GlassButton';
import { CheckCircle, AlertCircle, RefreshCw, Trash2 } from 'lucide-react';
import { apiClient as api } from '../../services/api';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface GatewaySetting {
  gateway: 'stripe' | 'paypal';
  active: boolean;
  verified: boolean;
  key_preview?: string;
}

interface StripeForm {
  publishable_key: string;
  secret_key: string;
  webhook_secret: string;
}

interface PayPalForm {
  client_id: string;
  client_secret: string;
  webhook_id: string;
  mode: 'sandbox' | 'live';
}

const EMPTY_STRIPE: StripeForm = {
  publishable_key: '',
  secret_key: '',
  webhook_secret: '',
};

const EMPTY_PAYPAL: PayPalForm = {
  client_id: '',
  client_secret: '',
  webhook_id: '',
  mode: 'sandbox',
};

const PaymentSettings: React.FC = () => {
  const [gateways, setGateways] = useState<GatewaySetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [stripeForm, setStripeForm] = useState<StripeForm>(EMPTY_STRIPE);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeFeedback, setStripeFeedback] = useState<{ msg: string; ok: boolean } | null>(null);
  const [stripeEditing, setStripeEditing] = useState(false);

  const [paypalForm, setPaypalForm] = useState<PayPalForm>(EMPTY_PAYPAL);
  const [paypalLoading, setPaypalLoading] = useState(false);
  const [paypalFeedback, setPaypalFeedback] = useState<{ msg: string; ok: boolean } | null>(null);
  const [paypalEditing, setPaypalEditing] = useState(false);

  const [activeGateway, setActiveGateway] = useState<string>('');
  const [savingActiveGateway, setSavingActiveGateway] = useState(false);

  useEffect(() => {
    loadGateways();
  }, []);

  const loadGateways = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/products/settings/gateways');
      setGateways(res.data.gateways || res.data);
      const active = (res.data.gateways || res.data).find((g: GatewaySetting) => g.active);
      if (active) setActiveGateway(active.gateway);
    } catch (err: any) {
      setError('Failed to load payment gateway settings.');
    } finally {
      setLoading(false);
    }
  };

  const getGateway = (name: 'stripe' | 'paypal'): GatewaySetting | undefined =>
    gateways.find(g => g.gateway === name);

  const handleSaveStripe = async () => {
    setStripeLoading(true);
    setStripeFeedback(null);
    try {
      await api.post('/products/settings/gateways', {
        gateway: 'stripe',
        credentials: { ...stripeForm },
      });
      const testRes = await api.post('/products/settings/gateways/stripe/test');
      const ok = testRes.data?.valid === true;
      setStripeFeedback({
        msg: ok
          ? 'Stripe connected and verified.'
          : `Saved, but test failed${testRes.data?.error ? `: ${testRes.data.error}` : '. Check your keys.'}`,
        ok,
      });
      await loadGateways();
      if (ok) setStripeEditing(false);
    } catch (err: any) {
      setStripeFeedback({
        msg: getApiError(err, 'Failed to save Stripe settings.'),
        ok: false,
      });
    } finally {
      setStripeLoading(false);
    }
  };

  const handleRemoveGateway = async (gateway: 'stripe' | 'paypal') => {
    if (!window.confirm(`Remove ${gateway} connection? This will disable payments through this gateway.`)) return;
    try {
      await api.delete(`/products/settings/gateways/${gateway}`);
      await loadGateways();
      if (gateway === 'stripe') {
        setStripeForm(EMPTY_STRIPE);
        setStripeFeedback(null);
        setStripeEditing(false);
      } else {
        setPaypalForm(EMPTY_PAYPAL);
        setPaypalFeedback(null);
        setPaypalEditing(false);
      }
    } catch (err: any) {
      setError(getApiError(err, `Failed to remove ${gateway}.`));
    }
  };

  const handleSavePayPal = async () => {
    setPaypalLoading(true);
    setPaypalFeedback(null);
    try {
      await api.post('/products/settings/gateways', {
        gateway: 'paypal',
        credentials: { ...paypalForm },
      });
      const testRes = await api.post('/products/settings/gateways/paypal/test');
      const ok = testRes.data?.valid === true;
      setPaypalFeedback({
        msg: ok
          ? 'PayPal connected and verified.'
          : `Saved, but test failed${testRes.data?.error ? `: ${testRes.data.error}` : '. Check your credentials.'}`,
        ok,
      });
      await loadGateways();
      if (ok) setPaypalEditing(false);
    } catch (err: any) {
      setPaypalFeedback({
        msg: getApiError(err, 'Failed to save PayPal settings.'),
        ok: false,
      });
    } finally {
      setPaypalLoading(false);
    }
  };

  const handleSetActiveGateway = async (gateway: string) => {
    setActiveGateway(gateway);
    setSavingActiveGateway(true);
    try {
      await api.post('/products/settings/gateways/active', { gateway });
    } catch {
      // non-fatal
    } finally {
      setSavingActiveGateway(false);
    }
  };

  if (loading) return <div className="products-loading">Loading payment settings...</div>;

  const stripeGateway = getGateway('stripe');
  const paypalGateway = getGateway('paypal');

  return (
    <div className="payment-settings">
      <div className="payment-settings-header">
        <h2>Payment Gateways</h2>
        <p>Connect your payment providers to start accepting payments from buyers.</p>
      </div>

      {error && <div className="products-error">{error}</div>}

      <div className="gateways-grid">
        {/* ---- Stripe ---- */}
        <div className={`gateway-card${stripeGateway?.verified ? ' connected' : ''}`}>
          <div className="gateway-card-header">
            <span className="gateway-name stripe">Stripe</span>
            {stripeGateway?.verified && !stripeEditing && (
              <span className="gateway-connected-badge">
                <CheckCircle size={13} /> Connected
              </span>
            )}
            {stripeGateway && !stripeGateway.verified && (
              <span className="gateway-unverified-badge">
                <AlertCircle size={13} /> Verification failed
              </span>
            )}
          </div>

          {stripeGateway?.verified && !stripeEditing ? (
            <>
              {stripeGateway.key_preview && (
                <div className="gateway-key-preview">
                  Secret key: <span>••••{stripeGateway.key_preview}</span>
                </div>
              )}
              <div className="gateway-card-actions">
                <GlassButton variant="secondary" size="sm" onClick={() => setStripeEditing(true)}>
                  Update Keys
                </GlassButton>
                <GlassButton
                  variant="danger"
                  size="sm"
                  icon={<Trash2 size={13} />}
                  onClick={() => handleRemoveGateway('stripe')}
                >
                  Remove
                </GlassButton>
              </div>
            </>
          ) : (
            <>
              <div className="gateway-form-group">
                <label>Publishable Key</label>
                <input
                  type="password"
                  placeholder="pk_live_..."
                  value={stripeForm.publishable_key}
                  onChange={e => setStripeForm(prev => ({ ...prev, publishable_key: e.target.value }))}
                  autoComplete="new-password"
                />
              </div>
              <div className="gateway-form-group">
                <label>Secret Key</label>
                <input
                  type="password"
                  placeholder="sk_live_..."
                  value={stripeForm.secret_key}
                  onChange={e => setStripeForm(prev => ({ ...prev, secret_key: e.target.value }))}
                  autoComplete="new-password"
                />
              </div>
              <div className="gateway-form-group">
                <label>Webhook Secret</label>
                <input
                  type="password"
                  placeholder="whsec_..."
                  value={stripeForm.webhook_secret}
                  onChange={e => setStripeForm(prev => ({ ...prev, webhook_secret: e.target.value }))}
                  autoComplete="new-password"
                />
              </div>
              {stripeFeedback && (
                <div className={`gateway-feedback ${stripeFeedback.ok ? 'success' : 'error'}`}>
                  {stripeFeedback.ok ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                  {stripeFeedback.msg}
                </div>
              )}
              <div className="gateway-card-actions">
                {stripeEditing && (
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    onClick={() => { setStripeEditing(false); setStripeFeedback(null); }}
                    disabled={stripeLoading}
                  >
                    Cancel
                  </GlassButton>
                )}
                <GlassButton
                  variant="primary"
                  size="sm"
                  onClick={handleSaveStripe}
                  loading={stripeLoading}
                  icon={<RefreshCw size={13} />}
                >
                  Save &amp; Test
                </GlassButton>
              </div>
            </>
          )}
        </div>

        {/* ---- PayPal ---- */}
        <div className={`gateway-card${paypalGateway?.verified ? ' connected' : ''}`}>
          <div className="gateway-card-header">
            <span className="gateway-name paypal">PayPal</span>
            {paypalGateway?.verified && !paypalEditing && (
              <span className="gateway-connected-badge">
                <CheckCircle size={13} /> Connected
              </span>
            )}
            {paypalGateway && !paypalGateway.verified && (
              <span className="gateway-unverified-badge">
                <AlertCircle size={13} /> Verification failed
              </span>
            )}
          </div>

          {paypalGateway?.verified && !paypalEditing ? (
            <>
              {paypalGateway.key_preview && (
                <div className="gateway-key-preview">
                  Client ID: <span>••••{paypalGateway.key_preview}</span>
                </div>
              )}
              <div className="gateway-card-actions">
                <GlassButton variant="secondary" size="sm" onClick={() => setPaypalEditing(true)}>
                  Update Credentials
                </GlassButton>
                <GlassButton
                  variant="danger"
                  size="sm"
                  icon={<Trash2 size={13} />}
                  onClick={() => handleRemoveGateway('paypal')}
                >
                  Remove
                </GlassButton>
              </div>
            </>
          ) : (
            <>
              <div className="gateway-form-group">
                <label>Client ID</label>
                <input
                  type="password"
                  placeholder="AX..."
                  value={paypalForm.client_id}
                  onChange={e => setPaypalForm(prev => ({ ...prev, client_id: e.target.value }))}
                  autoComplete="new-password"
                />
              </div>
              <div className="gateway-form-group">
                <label>Client Secret</label>
                <input
                  type="password"
                  placeholder="EK..."
                  value={paypalForm.client_secret}
                  onChange={e => setPaypalForm(prev => ({ ...prev, client_secret: e.target.value }))}
                  autoComplete="new-password"
                />
              </div>
              <div className="gateway-form-group">
                <label>Webhook ID</label>
                <input
                  type="text"
                  placeholder="Webhook ID from PayPal Developer"
                  value={paypalForm.webhook_id}
                  onChange={e => setPaypalForm(prev => ({ ...prev, webhook_id: e.target.value }))}
                />
              </div>
              <div className="gateway-form-group">
                <label>Mode</label>
                <select
                  value={paypalForm.mode}
                  onChange={e =>
                    setPaypalForm(prev => ({ ...prev, mode: e.target.value as 'sandbox' | 'live' }))
                  }
                >
                  <option value="sandbox">Sandbox (Testing)</option>
                  <option value="live">Live (Production)</option>
                </select>
              </div>
              {paypalFeedback && (
                <div className={`gateway-feedback ${paypalFeedback.ok ? 'success' : 'error'}`}>
                  {paypalFeedback.ok ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                  {paypalFeedback.msg}
                </div>
              )}
              <div className="gateway-card-actions">
                {paypalEditing && (
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    onClick={() => { setPaypalEditing(false); setPaypalFeedback(null); }}
                    disabled={paypalLoading}
                  >
                    Cancel
                  </GlassButton>
                )}
                <GlassButton
                  variant="primary"
                  size="sm"
                  onClick={handleSavePayPal}
                  loading={paypalLoading}
                  icon={<RefreshCw size={13} />}
                >
                  Save &amp; Test
                </GlassButton>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Active Gateway Selector */}
      {gateways.some(g => g.verified) && (
        <div className="active-gateway-selector">
          <h3>Active Checkout Gateway</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
            Select which payment gateway processes new purchases.
            {savingActiveGateway && (
              <span style={{ marginLeft: '8px', color: 'var(--text-muted)' }}>Saving...</span>
            )}
          </p>
          <div className="gateway-radio-group">
            {gateways
              .filter(g => g.verified)
              .map(g => (
                <label key={g.gateway} className="gateway-radio-label">
                  <input
                    type="radio"
                    name="active-gateway"
                    value={g.gateway}
                    checked={activeGateway === g.gateway}
                    onChange={() => handleSetActiveGateway(g.gateway)}
                  />
                  <span style={{ textTransform: 'capitalize', fontWeight: 500 }}>{g.gateway}</span>
                  {activeGateway === g.gateway && (
                    <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--success)', fontWeight: 600 }}>
                      Active
                    </span>
                  )}
                </label>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentSettings;
